import {
  Button,
  DataGrid,
  DataGridBody,
  DataGridRow,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
  Tooltip,
  Badge,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import {
  ChevronDoubleLeft20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  ChevronDoubleRight20Regular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { TeamRequest, RequestStatus } from "../../types";

interface RequestsDataGridProps {
  items: TeamRequest[];
  isApprover: boolean;
  currentUserId: string;
  getApprovalProgress: (item: TeamRequest) => { display: string; tooltip: string };
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSelectRequest: (item: TeamRequest) => void;
  onCancelRequest: (requestId: string) => void;
}

const useStyles = makeStyles({
  datagridContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: "auto",
    width: "100%",
    maxWidth: "100%"
  },
  datagridWrapper: {
    width: "100%",
    minWidth: "100%",
    display: "table",
    borderCollapse: "collapse"
  },
  datagridCell: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    verticalAlign: "middle",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: "background-color 0.2s ease",
    display: "table-cell",
    width: "auto"
  },
  datagridHeaderCell: {
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    textAlign: "left",
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "table-cell",
    width: "auto"
  },
  rowHover: {
    display: "table-row",
    width: "100%",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover
    }
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium
  },
  paginationInfo: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2
  },
  actionButtonsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingHorizontalXS,
    width: "100%",
    minHeight: "40px",
    flex: 1
  },
  actionButtonIcon: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  detailsActionButton: {
    color: tokens.colorNeutralForeground2,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground3
    }
  },
  cancelActionButton: {
    color: tokens.colorPaletteYellowForeground1,
    "&:hover": {
      backgroundColor: tokens.colorPaletteYellowBackground1
    }
  },
  approveActionButton: {
    color: tokens.colorPaletteGreenForeground1,
    "&:hover": {
      backgroundColor: tokens.colorPaletteGreenBackground1
    }
  },
  rejectActionButton: {
    color: tokens.colorPaletteRedForeground1,
    "&:hover": {
      backgroundColor: tokens.colorPaletteRedBackground1
    }
  }
});

export const RequestsDataGrid = ({
  items,
  isApprover,
  currentUserId,
  getApprovalProgress,
  currentPage,
  totalPages,
  onPageChange,
  onSelectRequest,
  onCancelRequest
}: RequestsDataGridProps) => {
  const { t } = useTranslation("requestsPage");
  const styles = useStyles();

  const getPrivacyLabel = (privacy: string) => {
    const labelKeys: Record<string, string> = {
      private: "privacyLabels.private",
      public: "privacyLabels.public"
    };
    return t(labelKeys[privacy] || "privacyLabels." + privacy);
  };

  const getStatusBadge = (status: RequestStatus) => {
    const colors: Record<RequestStatus, "success" | "danger" | "warning" | "informative" | "subtle"> = {
      [RequestStatus.Pending]: "warning",
      [RequestStatus.Approved]: "success",
      [RequestStatus.Rejected]: "danger",
      [RequestStatus.Created]: "success",
      [RequestStatus.CreatingTeam]: "informative",
      [RequestStatus.Completed]: "success",
      [RequestStatus.Failed]: "danger",
      [RequestStatus.Cancelled]: "subtle"
    };

    const labelKeys: Record<RequestStatus, string> = {
      [RequestStatus.Pending]: "statusBadges.pending",
      [RequestStatus.Approved]: "statusBadges.approved",
      [RequestStatus.Rejected]: "statusBadges.rejected",
      [RequestStatus.Created]: "statusBadges.created",
      [RequestStatus.CreatingTeam]: "statusBadges.creatingTeam",
      [RequestStatus.Completed]: "statusBadges.completed",
      [RequestStatus.Failed]: "statusBadges.failed",
      [RequestStatus.Cancelled]: "statusBadges.cancelled"
    };

    return (
      <Badge appearance="filled" color={colors[status]} style={{ whiteSpace: "nowrap" }}>
        {t(labelKeys[status])}
      </Badge>
    );
  };

  const columns = useMemo(
    () => [
      createTableColumn<TeamRequest>({
        columnId: "teamName",
        compare: (a, b) => a.teamName.localeCompare(b.teamName),
        renderHeaderCell: () => t("columns.teamName"),
        renderCell: (item) => (
          <TableCellLayout truncate title={item.teamName}>
            <Button
              appearance="transparent"
              size="small"
              onClick={() => onSelectRequest(item)}
              className={styles.detailsActionButton}
            >
              {item.teamName}
            </Button>
          </TableCellLayout>
        )
      }),
      createTableColumn<TeamRequest>({
        columnId: "privacy",
        compare: (a, b) => a.privacy.localeCompare(b.privacy),
        renderHeaderCell: () => t("columns.privacy"),
        renderCell: (item) => (
          <TableCellLayout truncate title={getPrivacyLabel(item.privacy)}>
            {getPrivacyLabel(item.privacy)}
          </TableCellLayout>
        )
      }),
      createTableColumn<TeamRequest>({
        columnId: "actions",
        renderHeaderCell: () => t("columns.actions"),
        renderCell: (item) => (
          <TableCellLayout>
            <div className={styles.actionButtonsContainer}>
              {isApprover && item.status === RequestStatus.Pending && (
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => onSelectRequest(item)}
                  className={styles.detailsActionButton}
                >
                  <CheckmarkCircle20Regular className="icon-text" />
                  <DismissCircle20Regular className="icon-text" />
                </Button>
              )}

              {!isApprover &&
                item.requesterId === currentUserId &&
                item.status === RequestStatus.Pending && (
                  <Tooltip content={t("actions.cancelTooltip")} relationship="label">
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<DismissCircle20Regular />}
                      onClick={() => onCancelRequest(item.id!)}
                      className={styles.cancelActionButton}
                    >
                      {t("actions.cancel")}
                    </Button>
                  </Tooltip>
                )}

              {(item.status === RequestStatus.Approved ||
                item.status === RequestStatus.Rejected ||
                item.status === RequestStatus.Cancelled) && (
                <span
                  style={{
                    color: tokens.colorNeutralForeground3,
                    fontSize: tokens.fontSizeBase200
                  }}
                >
                  —
                </span>
              )}
            </div>
          </TableCellLayout>
        )
      }),
      createTableColumn<TeamRequest>({
        columnId: "requester",
        compare: (a, b) => a.requesterEmail.localeCompare(b.requesterEmail),
        renderHeaderCell: () => t("columns.requester"),
        renderCell: (item) => (
          <TableCellLayout truncate title={item.requesterEmail}>
            {item.requesterEmail}
          </TableCellLayout>
        )
      }),
      createTableColumn<TeamRequest>({
        columnId: "description",
        compare: (a, b) => a.description.localeCompare(b.description),
        renderHeaderCell: () => t("columns.description"),
        renderCell: (item) => (
          <TableCellLayout truncate title={item.description}>
            {item.description}
          </TableCellLayout>
        )
      }),
      createTableColumn<TeamRequest>({
        columnId: "status",
        compare: (a, b) => a.status.localeCompare(b.status),
        renderHeaderCell: () => t("columns.status"),
        renderCell: (item) => <TableCellLayout>{getStatusBadge(item.status)}</TableCellLayout>
      }),
      createTableColumn<TeamRequest>({
        columnId: "teamsStatus",
        renderHeaderCell: () => t("columns.teamsStatus"),
        renderCell: (item) => {
          if (item.createdTeamId) {
            return (
              <TableCellLayout>
                <Tooltip
                  content={`${t("teamsStatusTooltips.provisioned")} (ID: ${item.createdTeamId})`}
                  relationship="label"
                >
                  <Badge
                    appearance="tint"
                    color="success"
                    style={{ cursor: "help", whiteSpace: "nowrap" }}
                  >
                    {t("teamsStatusBadges.provisioned")}
                  </Badge>
                </Tooltip>
              </TableCellLayout>
            );
          }
          if (item.status === RequestStatus.Approved) {
            return (
              <TableCellLayout>
                <Badge appearance="tint" color="warning" style={{ whiteSpace: "nowrap" }}>
                  {t("teamsStatusBadges.pending")}
                </Badge>
              </TableCellLayout>
            );
          }
          if (item.status === RequestStatus.Pending) {
            return (
              <TableCellLayout>
                <Badge appearance="outline" style={{ whiteSpace: "nowrap" }}>
                  {t("teamsStatusBadges.approval")}
                </Badge>
              </TableCellLayout>
            );
          }
          if (item.status === RequestStatus.Failed) {
            return (
              <TableCellLayout>
                <Tooltip content={item.errorMessage || t("messages.errorLoading")} relationship="label">
                  <Badge appearance="tint" color="danger" style={{ cursor: "help", whiteSpace: "nowrap" }}>
                    {t("teamsStatusBadges.failed")}
                  </Badge>
                </Tooltip>
              </TableCellLayout>
            );
          }
          return (
            <TableCellLayout>
              <Badge appearance="outline" style={{ whiteSpace: "nowrap" }}>
                {t("teamsStatusBadges.notApplicable")}
              </Badge>
            </TableCellLayout>
          );
        }
      }),
      createTableColumn<TeamRequest>({
        columnId: "approvals",
        renderHeaderCell: () => t("columns.approvals"),
        renderCell: (item) => {
          const progress = getApprovalProgress(item);
          return (
            <TableCellLayout>
              <Tooltip content={progress.tooltip} relationship="label">
                <span style={{ cursor: "help" }}>{progress.display}</span>
              </Tooltip>
            </TableCellLayout>
          );
        }
      }),
      createTableColumn<TeamRequest>({
        columnId: "date",
        compare: (a, b) =>
          new Date(a.submittedDate).getTime() - new Date(b.submittedDate).getTime(),
        renderHeaderCell: () => t("columns.date"),
        renderCell: (item) => (
          <TableCellLayout>
            {new Date(item.submittedDate).toLocaleDateString("fr-FR")}
          </TableCellLayout>
        )
      })
    ],
    [isApprover, currentUserId, onSelectRequest, onCancelRequest, getApprovalProgress, t, styles]
  );

  return (
    <div className={styles.datagridContainer}>
      <div className={styles.datagridWrapper}>
        <DataGrid
          items={items}
          columns={columns}
          sortable
          resizableColumns
          getRowId={(item) => item.id!}
          style={{ width: "100%", minWidth: "100%", borderCollapse: "collapse" }}
          columnSizingOptions={{
            teamName: { minWidth: 150, defaultWidth: 200 },
            privacy: { minWidth: 110, defaultWidth: 130 },
            actions: { minWidth: 150, defaultWidth: 180 },
            requester: { minWidth: 180, defaultWidth: 240 },
            description: { minWidth: 160, defaultWidth: 220 },
            status: { minWidth: 80, defaultWidth: 95 },
            teamsStatus: { minWidth: 130, defaultWidth: 150 },
            approvals: { minWidth: 120, defaultWidth: 150 },
            date: { minWidth: 80, defaultWidth: 95 }
          }}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell className={styles.datagridHeaderCell}>
                  {renderHeaderCell()}
                </DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody>
            {({ item, rowId }) => (
              <DataGridRow key={rowId} className={styles.rowHover}>
                {({ renderCell }) => (
                  <DataGridCell className={styles.datagridCell}>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>

      {totalPages > 1 && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            {t("pagination.page", { current: currentPage, total: totalPages })}
          </div>
          <div style={{ display: "flex", gap: tokens.spacingHorizontalS }}>
            <Tooltip content={t("pagination.firstPage")} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                disabled={currentPage === 1}
                onClick={() => onPageChange(1)}
                icon={<ChevronDoubleLeft20Regular />}
              />
            </Tooltip>
            <Tooltip content={t("pagination.previousPage")} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                icon={<ChevronLeft20Regular />}
              />
            </Tooltip>
            <Tooltip content={t("pagination.nextPage")} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                icon={<ChevronRight20Regular />}
              />
            </Tooltip>
            <Tooltip content={t("pagination.lastPage")} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(totalPages)}
                icon={<ChevronDoubleRight20Regular />}
              />
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};
